import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useUser } from "@/context/useUser";
import { getUserInfo } from "@/lib/storeManager";
import { projectToast } from "@project/lib/toast";
import {
  LayoutDashboard,
  Users,
  HandHeart,
  Heart,
  Stethoscope,
  MapPin,
  Crown,
  UserCheck,
  Calendar,
  Newspaper,
  Image as ImageIcon,
  MessageSquare,
  UtensilsCrossed,
  Mail,
  Building2,
  Cake,
  LogOut,
  Menu,
  X,
  Skull,
  ShieldCheck,
  UserCog,
  BarChart3,
  Layers,
  Megaphone,
  Repeat,
  Receipt,
  ReceiptText,
  Wallet,
  Home as HomeIcon,
  Sparkles,
  Package,
  HeartHandshake,
  Sprout,
  ShoppingBag,
  Boxes,
  Database,
  Briefcase,
  Send,
  CalendarDays,
  ClipboardList,
  HeartPulse,
  MapPinned,
  Link2,
  Truck,
  Soup,
  Tags,
  ListOrdered,
  ChevronDown,
  ChevronUp,
  Handshake,
} from "lucide-react";
import logo from "@project/assets/jivandhara-logo.png";

type NavItem = { to: string; icon: any; label: string; highlight?: boolean };
/** A group with `to` renders as a single direct link instead of a collapsible menu. */
type NavGroup = { label: string; icon: any; items: NavItem[]; to?: string };
const groups: NavGroup[] = [
  {
    label: "Overview",
    icon: LayoutDashboard,
    items: [
      { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
      { to: "/admin/reports", icon: BarChart3, label: "Reports" },
    ],
  },
  {
    label: "Donation Setup",
    icon: Layers,
    items: [
      {
        to: "/admin/donation-masters",
        icon: Layers,
        label: "Donation Masters",
      },
      {
        to: "/admin/item-categories",
        icon: Boxes,
        label: "Item Categories",
      },
      { to: "/admin/campaigns", icon: Megaphone, label: "Causes & Campaigns" },
      { to: "/admin/infrastructure", icon: HomeIcon, label: "Infrastructure" },
      { to: "/admin/seva", icon: Sparkles, label: "Seva / Sponsorship" },
      { to: "/admin/events", icon: Calendar, label: "Donation Events" },
      {
        to: "/admin/payment-settings",
        icon: Wallet,
        label: "Payment Settings",
      },
    ],
  },
  {
    label: "Donations",
    icon: HandHeart,
    items: [
      { to: "/admin/donors", icon: HandHeart, label: "Donors" },
      { to: "/admin/donations", icon: Heart, label: "Donation Records" },
      { to: "/admin/recurring", icon: Repeat, label: "Recurring Donations" },
      { to: "/admin/receipts", icon: Receipt, label: "Receipts (80G)" },
      {
        to: "/admin/inkind",
        icon: Package,
        label: "In-Kind Requests",
      },
      {
        to: "/admin/scrap-donations",
        icon: Truck,
        label: "Scrap Requests",
      },
    ],
  },
  {
    label: "Manual Receipt",
    icon: ReceiptText,
    to: "/admin/manual-receipts",
    items: [],
  },
  {
    label: "Admissions",
    icon: ClipboardList,
    items: [
      {
        to: "/admin/admission-applications",
        icon: ClipboardList,
        label: "Admission Applications",
      },
      {
        to: "/admin/admission-inquiry",
        icon: Mail,
        label: "Admission Inquiry Form",
      },
    ],
  },
  {
    label: "Resident Management",
    icon: Users,
    items: [
      { to: "/admin/residents", icon: Users, label: "Residents" },
      { to: "/admin/medical", icon: HeartPulse, label: "Medical Details" },
      { to: "/admin/visitors", icon: UserCheck, label: "Visitor Feedback" },
      {
        to: "/admin/visitor-inquiry",
        icon: ClipboardList,
        label: "Visitor Inquiry",
      },
      { to: "/admin/resident-death", icon: Skull, label: "Death Records" },
      { to: "/admin/adoption", icon: Crown, label: "Adoption Scheme" },
    ],
  },
  {
    label: "Modules",
    icon: Boxes,
    items: [
      { to: "/admin/elder-care", icon: HeartHandshake, label: "Elder Care" },
      { to: "/admin/gaushala", icon: Sprout, label: "Gaushala" },
      { to: "/admin/inventory", icon: Boxes, label: "Inventory & Assets" },
      { to: "/admin/hr-payroll", icon: Briefcase, label: "HR & Payroll" },
      { to: "/admin/communication", icon: Send, label: "Communication" },
      {
        to: "/admin/event-categories",
        icon: Tags,
        label: "Event Categories",
      },
      {
        to: "/admin/community-events",
        icon: CalendarDays,
        label: "Events",
      },
    ],
  },
  {
    label: "Healthcare",
    icon: Stethoscope,
    items: [
      { to: "/admin/healthcare", icon: Stethoscope, label: "Dashboard" },
      {
        to: "/admin/hospital-profile",
        icon: Building2,
        label: "Healthcare Details",
      },
      { to: "/admin/medical-staff", icon: Users, label: "Medical Staff" },
      {
        to: "/admin/healthcare-management",
        icon: ClipboardList,
        label: "Healthcare Management",
      },
      {
        to: "/admin/healthcare-donations",
        icon: HandHeart,
        label: "Donation List",
      },
    ],
  },
  {
    label: "Engagement",
    icon: MessageSquare,
    items: [
      { to: "/admin/punya-tithi", icon: Cake, label: "Punya Tithi" },
      { to: "/admin/tiffin", icon: UtensilsCrossed, label: "Tiffin Requests" },
      { to: "/admin/feedback", icon: MessageSquare, label: "Feedback" },
      { to: "/admin/inquiries", icon: Mail, label: "Inquiries" },
      { to: "/admin/csr-inquiries", icon: Handshake, label: "CSR Inquiries" },
      { to: "/admin/festivals", icon: CalendarDays, label: "Festivals" },
      {
        to: "/admin/jain-panchang",
        icon: Calendar,
        label: "Jain Panchang",
      },
    ],
  },
  {
    label: "Bhojanshala",
    icon: Soup,
    items: [
      {
        to: "/admin/bhojan-categories",
        icon: Tags,
        label: "Categories",
      },
      {
        to: "/admin/bhojan-packages",
        icon: Soup,
        label: "Packages",
      },
      {
        to: "/admin/bhojan-schemes",
        icon: ListOrdered,
        label: "Schemes",
      },
      {
        to: "/admin/bhojan-donations",
        icon: HandHeart,
        label: "Donation List",
      },
    ],
  },
  {
    label: "Gaushala",
    icon: Sprout,
    items: [
      {
        to: "/admin/gaushala-schemes",
        icon: ListOrdered,
        label: "Schemes",
      },
      {
        to: "/admin/gaushala-donations",
        icon: HandHeart,
        label: "Donation List",
      },
      {
        to: "/admin/vrindavan-gaushala",
        icon: Sprout,
        label: "Vrindavan Gir Gaushala",
      },
    ],
  },
  {
    label: "Tiffin Seva",
    icon: Soup,
    items: [
      {
        to: "/admin/tiffin-seva/dashboard",
        icon: LayoutDashboard,
        label: "Dashboard",
      },
      {
        to: "/admin/tiffin-seva/applications",
        icon: ClipboardList,
        label: "Inquiries & Applications",
      },
      {
        to: "/admin/tiffin-seva/donations",
        icon: HandHeart,
        label: "Donation",
      },
    ],
  },
  {
    label: "Gruh Udhyog",
    icon: ShoppingBag,
    items: [
      {
        to: "/admin/gruh-udhyog",
        icon: LayoutDashboard,
        label: "Dashboard",
      },
      {
        to: "/admin/product-categories",
        icon: Tags,
        label: "Categories",
      },
      {
        to: "/admin/products",
        icon: Package,
        label: "Products",
      },
      {
        to: "/admin/product-variants",
        icon: Boxes,
        label: "Variants",
      },
      {
        to: "/admin/gruh-udhyog-inventory",
        icon: Database,
        label: "Inventory",
      },
      {
        to: "/admin/product-orders",
        icon: ShoppingBag,
        label: "Product Orders",
      },
      {
        to: "/admin/franchise-enquiries",
        icon: Handshake,
        label: "Inquiries",
      },
    ],
  },
  {
    label: "Website Content",
    icon: Newspaper,
    items: [
      { to: "/admin/posts", icon: Newspaper, label: "Posts" },
      { to: "/admin/gallery", icon: ImageIcon, label: "Gallery" },
      { to: "/admin/trustees", icon: Crown, label: "Trustees" },
    ],
  },
  {
    label: "List Master",
    icon: MapPin,
    items: [
      { to: "/admin/districts", icon: MapPin, label: "Districts" },
      { to: "/admin/cities", icon: Building2, label: "Cities" },
      { to: "/admin/areas", icon: MapPinned, label: "Areas" },
      { to: "/admin/relationships", icon: Link2, label: "Relationships" },
      {
        to: "/admin/treatment-master",
        icon: Stethoscope,
        label: "Treatment Master",
      },
    ],
  },

  {
    label: "User Role & Permission",
    icon: ShieldCheck,
    items: [
      {
        to: "/admin/user-permissions",
        icon: UserCog,
        label: "User Permissions",
      },
    ],
  },
];

export function AdminShell({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const nav = useNavigate();
  const path = useRouterState({ select: s => s.location.pathname });
  const { isAuthenticated, logout } = useUser();
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    groups.forEach(g => {
      const hasActive = g.items.some(it => path === it.to || path.startsWith(it.to + "/"));
      if (hasActive) {
        initial[g.label] = true;
      }
    });
    return initial;
  });

  const toggleGroup = (label: string) => {
    setExpandedGroups(prev => {
      const next: Record<string, boolean> = {};
      const isCurrentlyExpanded = prev[label];
      if (!isCurrentlyExpanded) {
        next[label] = true;
      }
      return next;
    });
  };
  const navScrollRef = useRef<HTMLElement | null>(null);
  const SIDEBAR_SCROLL_KEY = "jivandhara-admin-sidebar-scroll";

  // Restore sidebar scroll on mount (preserved across route navigations)
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) {
      return;
    }
    const saved = window.sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    if (saved) {
      el.scrollTop = parseInt(saved, 10) || 0;
    }
    const onScroll = () => {
      window.sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(el.scrollTop));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const user = getUserInfo();
    setEmail(user?.email ?? "");
    if (!isAuthenticated) {
      nav({ to: "/admin" });
    }
  }, [isAuthenticated, nav]);

  const signOut = async () => {
    try {
      await logout();
      projectToast.success("Signed out successfully");
    } catch {
      projectToast.error("Could not sign out. Please try again.");
    } finally {
      nav({ to: "/admin" });
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => setOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex h-screen w-72 max-w-[85vw] shrink-0 flex-col border-r border-border bg-sidebar text-sidebar-foreground transition-transform duration-300 md:sticky md:top-0 md:w-64 md:max-w-none md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="flex items-center justify-between border-b border-sidebar-border p-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="ring-gold/60 grid h-10 w-10 place-items-center overflow-hidden rounded-full bg-background ring-2">
              <img
                src={logo}
                alt="Jivandhara logo"
                className="h-9 w-9 object-contain"
              />
            </div>
            <div>
              <div className="text-sm font-bold">Jivandhara</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Admin
              </div>
            </div>
          </Link>
          <button
            className="md:hidden"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav ref={navScrollRef} className="flex-1 overflow-y-auto p-3 space-y-1">
          {groups.map(g => {
            const isExpanded = !!expandedGroups[g.label];
            const hasActive = g.items.some(
              it => path === it.to || path.startsWith(it.to + "/")
            );

            // Group header style
            const headerBase =
              "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition cursor-pointer select-none w-full text-left";
            const headerCls = isExpanded
              ? "bg-primary text-primary-foreground"
              : "hover:bg-sidebar-accent text-sidebar-foreground";

            if (g.to) {
              const active = path === g.to || path.startsWith(g.to + "/");
              return (
                <div key={g.label} className="mb-2">
                  <Link
                    to={g.to}
                    resetScroll={false}
                    onClick={() => {
                      setExpandedGroups({});
                      setOpen(false);
                    }}
                    className={`${headerBase} ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-sidebar-accent text-sidebar-foreground"
                    }`}
                  >
                    <g.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">{g.label}</span>
                  </Link>
                </div>
              );
            }

            return (
              <div key={g.label} className="mb-2">
                <button
                  type="button"
                  onClick={() => toggleGroup(g.label)}
                  className={`${headerBase} ${headerCls}`}
                >
                  <g.icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{g.label}</span>
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 shrink-0 opacity-70" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
                  )}
                </button>

                {isExpanded && (
                  <div className="relative mt-1 ml-4 pl-3 flex flex-col gap-1">
                    <div className="absolute left-0 top-1 bottom-1 w-[2px] bg-gradient-to-b from-primary to-slate-200 rounded-full" />
                    {g.items.map(it => {
                      const active = path === it.to;
                      const itemBase =
                        "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition";
                      const itemCls = active
                        ? "border !border-primary bg-primary/10 !text-primary font-semibold"
                        : it.highlight
                          ? "bg-gradient-to-r from-gold/15 to-primary/10 text-foreground hover:from-gold/25 hover:to-primary/15"
                          : "hover:bg-sidebar-accent text-sidebar-foreground/80 hover:text-foreground";
                      return (
                        <Link
                          key={it.to + it.label}
                          to={it.to}
                          resetScroll={false}
                          onClick={() => setOpen(false)}
                          className={`${itemBase} ${itemCls}`}
                        >
                          <it.icon
                            className={`h-4 w-4 shrink-0 ${it.highlight && !active ? "text-gold" : ""}`}
                          />
                          <span className="flex-1 truncate">{it.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-sidebar-border p-3">
          <div className="truncate text-xs text-muted-foreground">{email}</div>
          <button
            onClick={signOut}
            className="text-foreground/80 mt-2 flex items-center gap-1 text-xs hover:text-primary"
          >
            <LogOut className="h-3 w-3" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="z-20 flex shrink-0 items-center justify-between gap-2 border-b border-border bg-card px-3 py-3 shadow-sm md:px-6">
          <div className="flex min-w-0 items-center gap-2 md:gap-3">
            <button
              className="-ml-1 p-1 md:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold text-foreground sm:text-lg md:text-xl">
                {title}
              </h1>
              {subtitle && (
                <p className="hidden truncate text-xs text-muted-foreground sm:block">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {actions && (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          )}
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-3 sm:p-4 md:p-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
